import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, CheckCircle2, AlertCircle, Download, Mail, Edit, Upload, Trash2, Calendar, Pencil, Check, X, Lock } from "lucide-react";
import { toast } from "sonner";
import EmailModal from "@/components/EmailModal";
import InvoiceUploadModal from "@/components/InvoiceUploadModal";
import { getBrandPillStyle } from "@/lib/brandColors";
import { calculateBrandStats as calcBrandStats } from "@/lib/brandStats";
import { toDMY } from "@/lib/dateOnly";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function QuotationDetail() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editOrderDate, setEditOrderDate] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  // Calculate brand-wise discount stats (shared logic — see lib/brandStats).
  const calculateBrandStats = (saleItems, freebieItems) => calcBrandStats(saleItems, freebieItems);

  const fetchQuotation = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}`, {
        withCredentials: true
      });
      setQuotation(response.data);
      
      // Fetch seller details for email subject
      if (response.data.seller_id) {
        try {
          const sellerResponse = await axios.get(`${API}/sellers/${response.data.seller_id}`, {
            withCredentials: true
          });
          setSeller(sellerResponse.data);
        } catch (sellerError) {
          // Seller fetch failed - silently continue
        }
      }
    } catch (error) {
      toast.error("Error fetching quotation");
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    fetchQuotation();
  }, [fetchQuotation]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${quotationId}`, {
        withCredentials: true
      });
      toast.success("Quotation deleted successfully");
      navigate("/quotations-log");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting quotation");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${quotation.quotation_id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quotation downloaded successfully");
    } catch (error) {
      toast.error("Error downloading quotation");
    }
  };

  const handleEmailClick = () => {
    setEmailModalOpen(true);
  };

  const startEditingDate = () => {
    // Format date to YYYY-MM-DD for the input
    const date = new Date(quotation.order_date);
    setEditOrderDate(date.toISOString().split('T')[0]);
    setEditingDate(true);
  };

  const cancelEditingDate = () => {
    setEditingDate(false);
    setEditOrderDate("");
  };

  const saveOrderDate = async () => {
    if (!editOrderDate) {
      toast.error("Please select a valid date");
      return;
    }
    
    setSavingDate(true);
    try {
      await axios.patch(
        `${API}/quotations/${quotationId}/order-date`,
        { order_date: editOrderDate },
        { withCredentials: true }
      );
      toast.success("Order date updated successfully");
      setEditingDate(false);
      fetchQuotation(); // Refresh the quotation data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating order date");
    } finally {
      setSavingDate(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: "bg-slate-100", text: "text-slate-800", label: "Draft" },
      mail_sent: { bg: "bg-blue-100", text: "text-blue-800", label: "Mail Sent" },
      processing: { bg: "bg-amber-100", text: "text-amber-800", label: "Processing" },
      ready_for_upload: { bg: "bg-teal-100", text: "text-teal-800", label: "Ready for Upload" },
      validation_failed: { bg: "bg-red-100", text: "text-red-800", label: "Validation Failed" },
      invoice_uploaded: { bg: "bg-success/10", text: "text-success", label: "Invoice Uploaded" }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!quotation) {
    return <div className="text-center py-8">Quotation not found</div>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/quotations-log")} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-[#0F0F0F] font-mono" data-testid="quotation-detail-title">{quotation.quotation_id}</h1>
                {(quotation.invoice_file_url || quotation.invoice_number) && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                    style={{ background: "#F3F4F6", color: "#4B5563", border: "0.5px solid #D1D5DB" }}
                    title="Quotation locked — invoice uploaded"
                    data-testid="quotation-locked-pill"
                  >
                    <Lock className="h-3 w-3" />
                    Locked
                  </span>
                )}
              </div>
              {/* Editable Order Date */}
              <div className="flex items-center gap-2 mt-1">
                {editingDate ? (
                  <>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={editOrderDate}
                      onChange={(e) => setEditOrderDate(e.target.value)}
                      className="w-40 h-8 text-sm"
                      data-testid="edit-order-date-input"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-success hover:text-success"
                      onClick={saveOrderDate}
                      disabled={savingDate}
                      data-testid="save-order-date-btn"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      onClick={cancelEditingDate}
                      disabled={savingDate}
                      data-testid="cancel-order-date-btn"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{new Date(quotation.order_date).toLocaleDateString()}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-accent"
                      onClick={startEditingDate}
                      data-testid="edit-order-date-btn"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Download is always available */}
            <Button onClick={handleDownload} variant="outline" data-testid="download-quotation-button">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            
            {/* Edit is always available */}
            <Button onClick={() => navigate(`/quotations/${quotationId}/edit`)} variant="outline" data-testid="edit-quotation-detail-button">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            
            {/* Send Email is ONLY available for draft status (new quotations) */}
            {quotation.status === "draft" && (
              <Button onClick={handleEmailClick} variant="outline" data-testid="send-email-button">
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            )}
            
            {/* Upload Invoice has been moved to Quotations Log (Actions → ⋯ menu) */}

            {/* Delete is always available */}
            <Button 
              onClick={() => setDeleteDialogOpen(true)} 
              variant="outline" 
              className="text-destructive border-destructive hover:bg-destructive/10"
              data-testid="delete-quotation-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quotation Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Quotation ID</p>
                <p className="font-mono font-semibold">{quotation.quotation_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Order Date</p>
                <p>{new Date(quotation.order_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Seller</p>
                <p className="font-semibold">{quotation.seller_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                {getStatusBadge(quotation.status)}
              </div>
              {quotation.invoice_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Date</p>
                  <p>{new Date(quotation.invoice_date).toLocaleDateString()}</p>
                </div>
              )}
              {quotation.dispatch_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Dispatch Date</p>
                  <p>{new Date(quotation.dispatch_date).toLocaleDateString()}</p>
                </div>
              )}
              {quotation.pickup_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Pickup Date</p>
                  <p data-testid="detail-pickup-date">{toDMY(quotation.pickup_date)}</p>
                </div>
              )}
              {quotation.rescheduled_pickup_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Rescheduled Pickup Date</p>
                  <p data-testid="detail-rescheduled-pickup-date">{toDMY(quotation.rescheduled_pickup_date)}</p>
                </div>
              )}
              {(quotation.rescheduled_pickup_date || quotation.pickup_date) && (
                <div>
                  <p className="text-sm text-muted-foreground">Final Pickup Date</p>
                  <p className="font-medium" data-testid="detail-final-pickup-date">{toDMY(quotation.rescheduled_pickup_date || quotation.pickup_date)}</p>
                </div>
              )}
              {quotation.transporter_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Transporter</p>
                  <p className="font-medium">{quotation.transporter_name}{quotation.transporter_code ? ` (${quotation.transporter_code})` : ''}</p>
                </div>
              )}
              {quotation.pickup_warehouse_name && (
                <div data-testid="quotation-detail-pickup-warehouse">
                  <p className="text-sm text-muted-foreground">Pickup Warehouse</p>
                  <p className="font-medium">{quotation.pickup_warehouse_name}</p>
                </div>
              )}
              <div className="col-span-2 md:col-span-4">
                <p className="text-sm text-muted-foreground">Shipping Address</p>
                <p>{quotation.seller_shipping_address}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sale Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Name</TableHead>
                    <TableHead>SKU ID</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>MRP</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Discount %</TableHead>
                    <TableHead>Total MRP</TableHead>
                    <TableHead>Invoice Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotation.items && quotation.items.length > 0 ? (
                    quotation.items.map((item) => (
                      <TableRow key={`item-${item.product_id}-${item.sku_id}`}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                        <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                        <TableCell className="font-mono">₹{item.mrp.toFixed(2)}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell className="font-mono">{item.discount}%</TableCell>
                        <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                        <TableCell className="font-mono font-semibold">₹{item.invoice_value.toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                        No items
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {quotation.freebies && quotation.freebies.length > 0 && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Freebies</CardTitle>
                {quotation.total_invoice_value > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Total Freebie %: <span className="font-mono font-semibold text-amber-600">
                      {((quotation.freebies_value / quotation.total_invoice_value) * 100).toFixed(2)}%
                    </span> of Invoice Value
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Name</TableHead>
                      <TableHead>SKU ID</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>MRP</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Total MRP</TableHead>
                      <TableHead>Freebie %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotation.freebies.map((item) => {
                      const freebiePercent = quotation.total_invoice_value > 0 
                        ? (item.total_mrp / quotation.total_invoice_value) * 100 
                        : 0;
                      return (
                        <TableRow key={`freebie-${item.product_id}-${item.sku_id}`}>
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.sku_id}</TableCell>
                          <TableCell>{(() => { const p = getBrandPillStyle(item.brand); return <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: p.bg, color: p.text }}>{item.brand}</span>; })()}</TableCell>
                          <TableCell className="font-mono">₹{item.mrp.toFixed(2)}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell className="font-mono">₹{item.total_mrp.toFixed(2)}</TableCell>
                          <TableCell className="font-mono font-semibold text-amber-600">{freebiePercent.toFixed(2)}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total MRP</p>
                <p className="text-2xl font-bold font-mono">₹{quotation.total_mrp_value.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Invoice Value</p>
                <p className="text-2xl font-bold font-mono">₹{quotation.total_invoice_value.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Discount Value</p>
                <p className="text-2xl font-bold font-mono">₹{quotation.total_discount_value.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Freebies Value</p>
                <p className="text-2xl font-bold font-mono">₹{quotation.freebies_value.toFixed(2)}</p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border-2 ${
              quotation.final_status === "valid" 
                ? "bg-success/10 border-success" 
                : "bg-destructive/10 border-destructive"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {quotation.final_status === "valid" ? (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  )}
                  <div>
                    <p className="font-semibold" data-testid="detail-overall-effective-discount">Overall Effective Discount: <span className="font-mono">{quotation.effective_discount.toFixed(2)}%</span></p>
                    <p className="text-xs text-muted-foreground">
                      Informational only · Approval is determined brand-wise via Slab Intelligence
                      {quotation.slab_validation?.quarter_label ? ` (${quotation.slab_validation.quarter_label})` : ""}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold ${
                    quotation.final_status === "valid" ? "text-success" : "text-destructive"
                  }`} data-testid="detail-discount-status">
                    {quotation.final_status === "valid" ? "Within Limit" : "Manager Approval Required"}
                  </p>
                </div>
              </div>
            </div>

            {/* Brand-wise Effective Discount + Slab Eligibility (from stored snapshot when available) */}
            {Array.isArray(quotation.slab_validation?.brands) && quotation.slab_validation.brands.length > 0 ? (
              <div className="p-4 bg-slate-50 rounded-lg border" data-testid="detail-slab-brand-panel">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
                  <p className="font-semibold text-sm">Brand-wise Effective Discount &amp; Slab Eligibility</p>
                  {quotation.slab_validation.quarter_label && (
                    <span className="text-xs text-muted-foreground">
                      Slab: {quotation.slab_validation.quarter_label}
                      {quotation.slab_validation.applicable_slab?.range_label ? ` · ${quotation.slab_validation.applicable_slab.range_label}` : " · No matching slab"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {quotation.slab_validation.brands.map((b) => {
                    const bad = b.status === "approval";
                    return (
                      <div key={b.brand} className={`p-3 rounded border-2 ${bad ? "bg-red-50 border-red-400" : "bg-green-50 border-green-400"}`} data-testid={`detail-brand-stat-${b.brand}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-[#0F0F0F]">{b.brand}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${bad ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`} data-testid={`detail-brand-status-${b.brand}`}>
                            {bad ? "Approval Required" : "Within Limit"}
                          </span>
                        </div>
                        <div className="mt-1.5 space-y-0.5 text-xs">
                          <p className="flex justify-between"><span className="text-muted-foreground">Contribution</span><span className="font-mono font-medium">{Number(b.contribution).toFixed(2)}%</span></p>
                          {b.has_contribution_rule && (
                            <p className="flex justify-between"><span className="text-muted-foreground">Required Contribution</span><span className="font-mono">{b.required_range_label || "—"}</span></p>
                          )}
                          <p className="flex justify-between"><span className="text-muted-foreground">Effective Discount</span><span className="font-mono font-medium">{Number(b.effective_discount).toFixed(2)}%</span></p>
                          <p className="flex justify-between"><span className="text-muted-foreground">Allowed Discount</span><span className={`font-mono font-medium ${bad ? "text-red-600" : "text-green-700"}`}>{b.allowed_label}</span></p>
                        </div>
                        {bad && b.reason && (
                          <p className="mt-1.5 text-[11px] text-red-600 leading-tight" data-testid={`detail-brand-reason-${b.brand}`}>{b.reason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              ((quotation.items && quotation.items.length > 0) || (quotation.freebies && quotation.freebies.length > 0)) && (() => {
                const brandStats = calculateBrandStats(quotation.items, quotation.freebies);
                return Object.keys(brandStats).length > 0 ? (
                  <div className="p-4 bg-slate-50 rounded-lg border">
                    <p className="font-semibold text-sm mb-3">Brand-wise Effective Discount:</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {Object.entries(brandStats).map(([brand, stats]) => (
                        <div key={brand} className="bg-white p-3 rounded border">
                          <p className="text-xs text-muted-foreground">{brand}</p>
                          <p className="font-mono font-semibold text-lg">{stats.discount.toFixed(1)}%</p>
                          <p className="text-xs text-muted-foreground">₹{stats.invoice.toLocaleString('en-IN')}</p>
                          {stats.freebieMrp > 0 && (
                            <p className="text-xs text-amber-600">+₹{stats.freebieMrp.toLocaleString('en-IN')} freebies</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()
            )}

            {/* Remarks Section */}
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-2">Remarks</p>
              <p className="text-base" data-testid="quotation-remarks">
                {quotation.remarks || quotation.approval_remarks || "-"}
              </p>
              {Array.isArray(quotation.remark_categories) && quotation.remark_categories.length > 0 && (
                <div className="mt-2 flex items-start gap-2 flex-wrap" data-testid="quotation-remark-categories">
                  <p className="text-sm text-muted-foreground mr-1">Categories:</p>
                  {quotation.remark_categories.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center rounded-full bg-[#E1F5EE] text-[#085041] border border-[#A5DDC9] px-2 py-0.5 text-[11px] font-medium"
                      data-testid={`view-quotation-remark-category-${c}`}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {quotation.approval_remarks && quotation.remarks && quotation.approval_remarks !== quotation.remarks && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-1">Approval Remarks</p>
                  <p className="text-base text-amber-700" data-testid="quotation-approval-remarks">
                    {quotation.approval_remarks}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Email Automation — auto-detected invoice/e-way details & validation (Phase 3, read-only) */}
        {(quotation.auto_invoice || quotation.auto_eway || quotation.validation_status) && (
          <Card data-testid="automation-detail-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Email Automation
                {quotation.validation_status && (
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    quotation.validation_status === "Validation Successful"
                      ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}
                    data-testid="automation-validation-badge">
                    {quotation.validation_status}
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground pt-1">
                Auto-extracted from the linked email thread for verification. These values are NOT
                applied to the quotation — use Upload Invoice to finalise.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {quotation.validation_status === "Validation Failed" && quotation.validation_reason && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="automation-fail-reason">
                  {quotation.validation_reason}
                </div>
              )}
              {quotation.auto_invoice && (
                <div>
                  <p className="text-sm font-medium mb-2">Detected Invoice</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Invoice No.</span><div data-testid="auto-inv-number">{quotation.auto_invoice.invoice_number || "—"}</div></div>
                    <div><span className="text-muted-foreground">Invoice Date</span><div data-testid="auto-inv-date">{quotation.auto_invoice.invoice_date || "—"}</div></div>
                    <div><span className="text-muted-foreground">Invoice Value</span><div data-testid="auto-inv-value">{quotation.auto_invoice.invoice_value != null ? `₹${Number(quotation.auto_invoice.invoice_value).toLocaleString("en-IN")}` : "—"}</div></div>
                    <div><span className="text-muted-foreground">Taxable Value</span><div>{quotation.auto_invoice.taxable_value != null ? `₹${Number(quotation.auto_invoice.taxable_value).toLocaleString("en-IN")}` : "—"}</div></div>
                    <div><span className="text-muted-foreground">GSTIN</span><div>{quotation.auto_invoice.gstin || "—"}</div></div>
                  </div>
                </div>
              )}
              {quotation.auto_eway && (
                <div>
                  <p className="text-sm font-medium mb-2">Detected E-Way Bill</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-muted-foreground">E-Way No.</span><div data-testid="auto-eway-number">{quotation.auto_eway.eway_number || "—"}</div></div>
                    <div><span className="text-muted-foreground">Date</span><div>{quotation.auto_eway.eway_date || "—"}</div></div>
                    <div><span className="text-muted-foreground">Valid Upto</span><div>{quotation.auto_eway.validity || "—"}</div></div>
                    <div><span className="text-muted-foreground">Vehicle</span><div>{quotation.auto_eway.vehicle_number || "—"}</div></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {quotation && (
        <>
          <EmailModal
            quotation={quotation}
            seller={seller}
            open={emailModalOpen}
            onClose={() => setEmailModalOpen(false)}
            onSuccess={fetchQuotation}
          />
          <InvoiceUploadModal
            quotation={quotation}
            open={invoiceUploadOpen}
            onClose={() => setInvoiceUploadOpen(false)}
            onSuccess={fetchQuotation}
          />
        </>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this quotation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete quotation {quotation?.quotation_id} from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
